// Module-level singleton holding the current wizard draft.
// Also persisted to localStorage so wizard state survives reloads, tab
// closes, and back-button navigation. See `persistDraftFor` / `loadDraftFor`.

// Industry type is the canonical option set defined in @/lib/industries.
// Keeping the type aliased here preserves every existing import path.
import type { IndustryOption } from "./industries";
export type Industry = IndustryOption;

export type Region =
  | "US Midwest"
  | "US Northeast"
  | "US Southeast"
  | "US West"
  | "US Southwest"
  | "Canada"
  | "Mexico"
  | "Other";

// Loosened from a hard-coded synthetic-name union. Real AE names come from
// the live tenant roster via useTenantReps; the storage shape now also
// carries repId (the user UUID) so we can persist owner_user_id on save.
export type RepName = string;

export type CEStatus = "yes" | "suspected" | "no";

export type StakeholderRole =
  | "Decision-maker"
  | "Influencer"
  | "Champion"
  | "Blocker"
  | "Coach"
  | "End-user";

export interface Stakeholder {
  name: string;
  title: string;
  role: StakeholderRole | "";
  engagement: number | undefined; // 1-5, undefined until user touches
}

export interface MpactRow {
  key: string;
  letter: string;
  label: string;
  score: number; // 0-100
  evidence: string;
}

export interface PlanAction {
  action: string;
  owner: string;
  due: string;
}

export interface AccountDraft {
  // Step 1
  company: string;
  industry: Industry | "";
  region: Region | "";
  employees: number | "";
  arrPotential: number | "";
  rep: RepName | "";
  repId?: string;

  // Step 2
  ceStatus: CEStatus | undefined;
  ceDescription: string;
  ceDecisionDate: string; // ISO yyyy-mm-dd
  ceConfidence: number | undefined;

  // Step 3
  stakeholders: Stakeholder[];

  // Step 4
  mpact: MpactRow[];

  // Step 5
  approach: string;
  risks: string[];
  next30: PlanAction[];
  next60: PlanAction[];
  next90: PlanAction[];

  // Meta
  createdAt: string;
  submittedAt?: string; // set when the user generates the report; signals
                        // that the draft is the "completed snapshot" used
                        // by the Back-to-wizard path on the plan report.
}

let currentDraft: AccountDraft | null = null;
export const getDraft = (): AccountDraft | null => currentDraft;
export const setDraft = (d: AccountDraft) => {
  currentDraft = d;
};
export const clearDraft = () => {
  currentDraft = null;
};

// ── localStorage persistence ────────────────────────────────────────────
// Storage key: `aip:wizard-draft:<user-id>:<wizard-name>` so each signed-in
// user/tenant has an isolated draft per wizard flow.

const DRAFT_KEY_PREFIX = "aip:wizard-draft";

export function draftStorageKey(userKey: string, wizardName: string): string {
  return `${DRAFT_KEY_PREFIX}:${userKey}:${wizardName}`;
}

export function persistDraftFor(
  userKey: string,
  wizardName: string,
  d: AccountDraft,
): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(
      draftStorageKey(userKey, wizardName),
      JSON.stringify(d),
    );
  } catch {
    /* localStorage may be unavailable (quota, private mode); ignore */
  }
}

export function loadDraftFor(
  userKey: string,
  wizardName: string,
): AccountDraft | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(draftStorageKey(userKey, wizardName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AccountDraft;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPersistedDraftFor(userKey: string, wizardName: string): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(draftStorageKey(userKey, wizardName));
  } catch {
    /* ignore */
  }
}

// Heuristic: a draft is "meaningful" (worth offering to resume) if the user
// has typed anything that distinguishes it from an empty form.
export function draftHasContent(d: AccountDraft | null): boolean {
  if (!d) return false;
  if (d.company?.trim()) return true;
  if (d.ceDescription?.trim()) return true;
  if (d.ceDecisionDate) return true;
  if (d.industry || d.region || d.rep) return true;
  if (typeof d.employees === "number" && d.employees > 0) return true;
  if (typeof d.arrPotential === "number" && d.arrPotential > 0) return true;
  if (d.approach?.trim()) return true;
  if (d.risks?.some((r) => r.trim())) return true;
  if (d.stakeholders?.some((s) => s.name?.trim())) return true;
  if (d.mpact?.some((r) => r.evidence?.trim())) return true;
  if (
    ["next30", "next60", "next90"].some((k) =>
      (d as any)[k]?.some((a: PlanAction) => a.action?.trim() || a.owner?.trim() || a.due?.trim()),
    )
  ) {
    return true;
  }
  return false;
}

export const DEFAULT_MPACT: MpactRow[] = [
  { key: "M", letter: "M", label: "Methodology", score: 0, evidence: "" },
  { key: "P", letter: "P", label: "Process / Prioritization", score: 0, evidence: "" },
  { key: "A", letter: "A", label: "Accountability", score: 0, evidence: "" },
  { key: "C", letter: "C", label: "Cadence / Coaching", score: 0, evidence: "" },
  { key: "T", letter: "T", label: "Timing / Timeline management", score: 0, evidence: "" },
];

export function emptyDraft(): AccountDraft {
  return {
    company: "",
    industry: "",
    region: "",
    employees: "",
    arrPotential: "",
    rep: "",
    ceStatus: undefined,
    ceDescription: "",
    ceDecisionDate: "",
    ceConfidence: undefined,
    stakeholders: [
      { name: "", title: "", role: "", engagement: undefined },
      { name: "", title: "", role: "", engagement: undefined },
      { name: "", title: "", role: "", engagement: undefined },
    ],
    mpact: DEFAULT_MPACT.map((r) => ({ ...r })),
    approach: "",
    risks: ["", "", ""],
    next30: [
      { action: "", owner: "", due: "" },
      { action: "", owner: "", due: "" },
      { action: "", owner: "", due: "" },
    ],
    next60: [
      { action: "", owner: "", due: "" },
      { action: "", owner: "", due: "" },
      { action: "", owner: "", due: "" },
    ],
    next90: [
      { action: "", owner: "", due: "" },
      { action: "", owner: "", due: "" },
      { action: "", owner: "", due: "" },
    ],
    createdAt: new Date().toISOString(),
  };
}

// Hardcoded sample for /plan-report/sample
export function sampleDraft(): AccountDraft {
  const today = new Date();
  const decision = new Date(today);
  decision.setDate(decision.getDate() + 75);
  const isoDate = decision.toISOString().slice(0, 10);

  return {
    company: "NorthMap Industrial",
    industry: "Industrial Manufacturing",
    region: "US West",
    employees: 1250,
    arrPotential: 480000,
    rep: "Will Winsome",
    ceStatus: "yes",
    ceDescription:
      "RFP issued by NorthMap board to consolidate analytics tooling by end of Q3. Procurement evaluating three vendors. Decision committee meets every two weeks.",
    ceDecisionDate: isoDate,
    ceConfidence: 64,
    stakeholders: [
      { name: "Eleanor Hart", title: "CFO", role: "Decision-maker", engagement: 4 },
      { name: "Marcus Yi", title: "CIO", role: "Decision-maker", engagement: 3 },
      { name: "Priya Shah", title: "VP Analytics", role: "Champion", engagement: 5 },
      { name: "Daniel Reyes", title: "Director, FP&A", role: "Influencer", engagement: 4 },
      { name: "Karen Volkov", title: "Procurement Lead", role: "Blocker", engagement: 2 },
    ],
    mpact: [
      { key: "M", letter: "M", label: "Methodology", score: 72, evidence: "Structured deal methodology in place; CFO and CIO aligned on evaluation criteria." },
      { key: "P", letter: "P", label: "Process / Prioritization", score: 80, evidence: "Three disparate tools, manual reconciliation costing ~$420k annually — prioritized for resolution." },
      { key: "A", letter: "A", label: "Accountability", score: 55, evidence: "Decision committee identified; board sign-off still required for >$300k." },
      { key: "C", letter: "C", label: "Cadence / Coaching", score: 68, evidence: "Active RFP with end-of-Q3 deadline; bi-weekly committee cadence documented." },
      { key: "T", letter: "T", label: "Timing / Timeline management", score: 70, evidence: "75-day window aligns with our delivery model; board mandate creates hard deadline." },
    ],
    approach:
      "Lead with the intelligence-layer narrative; CFO is data-skeptic — open with measurable cost of inaction. Position Mi6 as the calibration layer that reads the deal, not the dashboard. Pull VP Analytics in as champion; isolate Procurement Lead with parallel security/legal track.",
    risks: [
      "Procurement Lead actively favors incumbent vendor on legacy contract terms.",
      "Board sign-off threshold may force scope reduction below ARR target.",
      "Competing internal data-platform project could absorb Q4 attention.",
    ],
    next30: [
      { action: "Run executive briefing with CFO and CIO", owner: "Will Winsome", due: "Week 2" },
      { action: "Lock champion alignment session with VP Analytics", owner: "Will Winsome", due: "Week 1" },
      { action: "Submit security questionnaire and SOC2 package", owner: "Michael (BDR)", due: "Week 3" },
    ],
    next60: [
      { action: "Deliver tailored ROI analysis to FP&A director", owner: "Will Winsome", due: "Week 5" },
      { action: "Run technical proof-of-value with analytics team", owner: "Solutions Eng", due: "Week 6" },
      { action: "Mutual close plan reviewed with decision committee", owner: "Will Winsome", due: "Week 8" },
    ],
    next90: [
      { action: "Final pricing and contract red-line cycle", owner: "Deal Desk", due: "Week 10" },
      { action: "Board presentation prep with CFO", owner: "Will Winsome", due: "Week 11" },
      { action: "Close and kickoff handoff to Customer Success", owner: "Will Winsome", due: "Week 13" },
    ],
    createdAt: today.toISOString(),
  };
}
